/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';

// 🚨 Important! `TableTh` shares styles with `TableTd`. While this is
// generally frowned upon, it's fairly crucial to keep the styles between
// the components *nsync.
import { Styles } from './TableTd';

type Props = {
  /**
   * The content to render within the component.
   */
  children: React.ReactNode;
  /**
   * Styling hook.
   */
  className?: string;
  /**
   * Whether to freeze the cell during scrolling.
   */
  freeze?: 'row' | 'col' | 'row col';
  /**
   * Whether to render a shadow when the cell is frozen.
   */
  shadowed?: 'row' | 'col' | 'row col';
  /**
   * The `headers` attribute for `th`. Contains a space-separated list
   * of `id`s that reference `th` tags that describe `th`. Needed for a11y.
   */
  headers?: string;
  /**
   * The `id` attribute for `th`. Used for accessibility.
   */
  id?: string;
  /**
   * The `role` attribute for `th`, indicating which axis `th` applies to.
   */
  role?: 'columnheader' | 'rowheader';
  /**
   * The scope attribute for `th`, indicating what `th` applies to.
   */
  scope?: 'col' | 'row';
};

const defaultProps = {
  role: 'columnheader',
};

/**
 * An individual `th` table cell. 🚨 Note that this component shares a
 * lot of commonality with `TableTd`, however they have different purposes
 * and `Props` requirements so they're defined distinctly.
 */
export const TableTh = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  return (
    <th
      css={[Styles.root, props.className]}
      data-freeze={props.freeze}
      data-shadowed={props.shadowed}
      headers={props.headers}
      id={props.id}
      role={props.role}
      scope={props.scope}
    >
      {props.children}
    </th>
  );
};
